import { AppConfig } from "@/infrastructure/services/config/WebApp.config";
import { Button, Chip, Division, Flex, Paragraph, Span, Tooltip, SelectOption, Icon, Icons } from "@ir/ui";
import { DateAdapter } from "@/infrastructure/adapters/dayJs";
import { DateService } from "@/core/services/Date.service";
import { FilterInfo } from "@/store/deduction/deductionFilter";
import { ReactNode, FC, useMemo, useCallback } from "react";
import { useDeductionFilter, useDeductionDrawer } from "@/presentation/deduction/hooks";
import { useWorkspace } from "@/hooks";
import { TagNavigator } from "@/presentation/deduction/components";
import { FilterDateRange } from "./interfaces/IDeductionFilter";
import { GetFilterDateRangeLabel } from "./utils";
import { getEmptyFilterInfo, mapMonthsToFilterDateRange } from "@/store/deduction/deductionFilter/utils";
import { StringAdapter } from "@/infrastructure/adapters/string";
import {
  applyFilterKeyDeletion,
  FilterSetInfo,
  getEffectiveCheckDateRange,
  isAccountingRemittanceFilterInfo,
  isApprovalFilterInfo,
  isDisputeTrackingFilterInfo,
  isPromotionsFilterInfo,
} from "./DeductionFilterSet.utils";

const tagColor = "var(--grey-500)";
const valueColor = "var(--primary-600)";

const dateService = new DateService(new DateAdapter());
const stringAdapter = new StringAdapter();
const currentDate = dateService.getCurrentDate().toISOString();

interface TagFilterData {
  key: string;
  tag: ReactNode;
}

export interface FilterSetApi {
  filterInfo: FilterSetInfo;
  isFilterApplied: boolean;
  setFilter: (info: FilterSetInfo) => void;
  clearFilter: (defaultFilter?: FilterSetInfo) => void;
  setIsFilterApplied: (value: boolean) => void;
  setCurrentPage: (page: number) => void;
  searchHandler: () => Promise<void>;
  defaultFilterInfo: FilterSetInfo;
  isDefaultFilter: (info: FilterSetInfo) => boolean;
}

interface DeductionFilterSetProps {
  className?: string;
  groupByCheck: boolean;
  openFilter: () => void;
  /** When provided, use this instead of useDeductionFilter (e.g. for Approval page) */
  filterApi?: FilterSetApi;
}

const DeductionFilterSet: FC<DeductionFilterSetProps> = ({ className, groupByCheck, openFilter, filterApi }) => {
  const { settings } = useWorkspace();
  const deductionFilter = useDeductionFilter();
  const { setDrawerOpen, isDrawerOpen } = useDeductionDrawer();

  const isDefaultFilter = filterApi?.isDefaultFilter ?? deductionFilter.isDefaultFilter;
  const isFilterApplied = filterApi?.isFilterApplied ?? deductionFilter.isFilterApplied;
  const filterInfo = filterApi?.filterInfo ?? deductionFilter.filterInfo;
  const isApprovalMode = filterApi != null && isApprovalFilterInfo(filterInfo);
  const isPromotionsMode = filterApi != null && isPromotionsFilterInfo(filterInfo);
  const isDisputeTrackingMode = filterApi != null && isDisputeTrackingFilterInfo(filterInfo);
  const isAccountingRemittanceMode = filterApi != null && isAccountingRemittanceFilterInfo(filterInfo);
  const deductionClearFilter = useCallback<FilterSetApi["clearFilter"]>(
    (defaultFilter?) => deductionFilter.clearFilter(defaultFilter as FilterInfo | undefined),
    [deductionFilter]
  );
  const deductionSetFilter = useCallback<FilterSetApi["setFilter"]>(
    info => deductionFilter.setFilter(info as FilterInfo),
    [deductionFilter]
  );
  const clearFilter = filterApi?.clearFilter ?? deductionClearFilter;
  const setFilter = filterApi?.setFilter ?? deductionSetFilter;
  const setIsFilterApplied = filterApi?.setIsFilterApplied ?? deductionFilter.setIsFilterApplied;
  const setCurrentPage =
    filterApi?.setCurrentPage ??
    (groupByCheck ? deductionFilter.setRemittanceCurrentPage : deductionFilter.setRemittanceItemCurrentPage);
  const searchHandler =
    filterApi?.searchHandler ?? (groupByCheck ? deductionFilter.getRemittances : deductionFilter.getRemittanceItems);

  const defaultCheckDateRangeInMonths =
    settings?.defaultFilterCheckDateRange ?? AppConfig.getInstance().DEFAULT_FILTER_CHECK_DATE_RANGE;
  const defaultCheckDateRangePreset = mapMonthsToFilterDateRange(defaultCheckDateRangeInMonths);
  const isNoDefaultDateRange = defaultCheckDateRangeInMonths === -1;
  const defaultCheckToDate = useMemo(
    () =>
      isNoDefaultDateRange
        ? null
        : dateService.format(dateService.add(currentDate, 1, "month").toISOString(), "YYYY-MM-DD"),
    [isNoDefaultDateRange]
  );
  const defaultCheckFromDate = useMemo<string | null>(
    () =>
      isNoDefaultDateRange
        ? null
        : dateService.format(
            dateService.subtract(currentDate, defaultCheckDateRangeInMonths, "month").toISOString(),
            "YYYY-MM-DD"
          ),
    [defaultCheckDateRangeInMonths, isNoDefaultDateRange]
  );

  const deductionDefaultFilterInfo = useMemo<FilterInfo>(() => {
    const base = getEmptyFilterInfo();
    return {
      ...base,
      checkFromDate: defaultCheckFromDate,
      checkToDate: defaultCheckToDate,
      checkDateRange: defaultCheckDateRangePreset,
    };
  }, [defaultCheckDateRangePreset, defaultCheckFromDate, defaultCheckToDate]);

  const defaultFilterInfo = filterApi?.defaultFilterInfo ?? deductionDefaultFilterInfo;

  const getTooltipContent = useCallback((array: SelectOption[] | string[]) => {
    if (!array) return;
    return array.map(item => {
      const itemValue = typeof item === "string" ? item : item.value;
      const itemLabel = typeof item === "string" ? item : item.label;
      return (
        <Paragraph className="m-0" key={itemValue}>
          {itemLabel}
        </Paragraph>
      );
    });
  }, []);

  const formatDate = useCallback(
    (date: string) => {
      return dateService.format(date, settings?.displayDateFormat ?? AppConfig.getInstance().DEFAULT_DATE_FORMAT);
    },
    [settings?.displayDateFormat]
  );

  const getTagLabelByString = useCallback((value: string) => {
    return stringAdapter.split(value).length > 1
      ? `${stringAdapter.split(value)[0]} +${stringAdapter.split(value).length - 1}`
      : stringAdapter.split(value).join(", ");
  }, []);

  const getTagLabelByArray = useCallback((array: SelectOption[]) => {
    if (!array) return "";

    if (array.length >= 2) {
      return `${array[0].label} +${array.length - 1}`;
    }

    return array.map(item => item.label).join(", ");
  }, []);

  const createTag = useCallback(
    (key: string, label: string, value: string, tooltipContent?: ReactNode) => {
      const displayValue = (
        <Span style={{ color: tagColor, fontWeight: 700 }}>
          {label}: <b style={{ color: valueColor, fontWeight: 700 }}>{value}</b>
        </Span>
      );
      const tooltip = tooltipContent ? (
        <Tooltip content={tooltipContent} placement="bottom">
          {
            <Button noStyle onPress={isDrawerOpen ? undefined : openFilter} className="m-0">
              {displayValue}
            </Button>
          }
        </Tooltip>
      ) : (
        <Button noStyle onPress={isDrawerOpen ? undefined : openFilter} className="m-0">
          {displayValue}
        </Button>
      );

      const chip = (
        <Chip
          shape="square"
          color="grey"
          label={tooltip}
          className="bg-white border-color-grey-100"
          style={{ boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)" }}
        />
      );

      return {
        key,
        tag: chip,
      };
    },
    [isDrawerOpen, openFilter]
  );

  const addCheckDateTag = useCallback<
    (addTag: (key: string, label: string, value: string, array?: SelectOption[] | string[]) => void) => void
  >(
    (addTag: (key: string, label: string, value: string, array?: SelectOption[] | string[]) => void) => {
      if (!("checkFromDate" in filterInfo)) return;

      const useDefaultDates =
        !isApprovalMode && !isPromotionsMode && !isDisputeTrackingMode && !isAccountingRemittanceMode && !isFilterApplied;
      const effectiveCheckFromDate = filterInfo.checkFromDate ?? (useDefaultDates ? defaultCheckFromDate : null);
      const effectiveCheckToDate = filterInfo.checkToDate ?? (useDefaultDates ? defaultCheckToDate : null);
      if (!effectiveCheckFromDate || !effectiveCheckToDate) return;

      if (!isApprovalMode && !isPromotionsMode && !isDisputeTrackingMode && !isAccountingRemittanceMode) {
        const hasExplicitDates = Boolean(filterInfo.checkFromDate && filterInfo.checkToDate);
        const shouldShowCheckDateTag = !isNoDefaultDateRange || hasExplicitDates;
        if (!shouldShowCheckDateTag) return;
      }

      const datesMatchDefault =
        useDefaultDates &&
        effectiveCheckFromDate === defaultCheckFromDate &&
        effectiveCheckToDate === defaultCheckToDate;
      const effectiveCheckDateRange = getEffectiveCheckDateRange(
        filterInfo,
        isFilterApplied,
        datesMatchDefault,
        defaultCheckDateRangePreset
      );
      const checkDatePresetLabel = effectiveCheckDateRange ? GetFilterDateRangeLabel(effectiveCheckDateRange) : null;

      addTag(
        "checkDate",
        "Check Date",
        checkDatePresetLabel ?? `${formatDate(effectiveCheckFromDate)} - ${formatDate(effectiveCheckToDate)}`
      );
    },
    [
      filterInfo,
      isFilterApplied,
      defaultCheckFromDate,
      defaultCheckToDate,
      defaultCheckDateRangePreset,
      isNoDefaultDateRange,
      formatDate,
      isApprovalMode,
      isPromotionsMode,
      isDisputeTrackingMode,
      isAccountingRemittanceMode,
    ]
  );

  const addInvoiceDateTag = useCallback(
    (addTag: (key: string, label: string, value: string, array?: SelectOption[] | string[]) => void) => {
      if (!("invoiceFromDate" in filterInfo) || !filterInfo.invoiceFromDate || !filterInfo.invoiceToDate) return;

      const invoiceDateLabel =
        GetFilterDateRangeLabel(filterInfo.invoiceDateRange as FilterDateRange) ??
        `${formatDate(filterInfo.invoiceFromDate)} - ${formatDate(filterInfo.invoiceToDate)}`;

      addTag("invoiceFromDate-invoiceToDate", "Invoice Date", invoiceDateLabel);
    },
    [filterInfo, formatDate]
  );

  const addArrayFilterTag = useCallback(
    (
      addTag: (key: string, label: string, value: string, array?: SelectOption[] | string[]) => void,
      key: string,
      label: string,
      array: SelectOption[] | undefined
    ) => {
      if (!array || array.length === 0) return;
      addTag(key, label, getTagLabelByArray(array), array);
    },
    [getTagLabelByArray]
  );

  const addPoNumberTag = useCallback(
    (
      addTag: (key: string, label: string, value: string, array?: SelectOption[] | string[]) => void,
      deductionInfo?: FilterInfo
    ) => {
      const info = deductionInfo ?? filterInfo;
      if (!("poNumberSearch" in info) || !info.poNumberSearch) return;
      addTag(
        "poNumberSearch",
        "PO Number",
        getTagLabelByString(info.poNumberSearch),
        stringAdapter.split(info.poNumberSearch)
      );
    },
    [filterInfo, getTagLabelByString]
  );

  const addSearchTag = useCallback(
    (addTag: (key: string, label: string, value: string, array?: SelectOption[] | string[]) => void) => {
      if (isPromotionsFilterInfo(filterInfo) || isDisputeTrackingFilterInfo(filterInfo)) {
        if (!filterInfo.search?.trim()) return;
        addTag("search", "Search", filterInfo.search.trim());
        return;
      }
      if (isAccountingRemittanceFilterInfo(filterInfo)) {
        if (!filterInfo.search?.trim()) return;
        addTag("search", "Search", getTagLabelByString(filterInfo.search), stringAdapter.split(filterInfo.search));
        return;
      }
      if (!isApprovalFilterInfo(filterInfo) || !filterInfo.search?.trim()) return;
      addTag("search", "Search", getTagLabelByString(filterInfo.search), stringAdapter.split(filterInfo.search));
    },
    [filterInfo, getTagLabelByString]
  );

  const addStartDateTag = useCallback(
    (addTag: (key: string, label: string, value: string, array?: SelectOption[] | string[]) => void) => {
      if (!isPromotionsFilterInfo(filterInfo) || !filterInfo.startFromDate || !filterInfo.startToDate) return;

      const startDateLabel =
        GetFilterDateRangeLabel(filterInfo.startDateRange as FilterDateRange) ??
        `${formatDate(filterInfo.startFromDate)} - ${formatDate(filterInfo.startToDate)}`;

      addTag("startFromDate-startToDate", "Start Date", startDateLabel);
    },
    [filterInfo, formatDate]
  );

  const tagsFilterData = useMemo(() => {
    const tagsData: TagFilterData[] = [];

    const addTag = (key: string, label: string, value: string, array?: SelectOption[] | string[]) => {
      const tooltipContent = array && array.length > 1 ? getTooltipContent(array) : null;
      tagsData.push(createTag(key, label, value, tooltipContent));
    };

    addCheckDateTag(addTag);
    addInvoiceDateTag(addTag);
    addArrayFilterTag(addTag, "purchaser", "Purchaser", filterInfo.purchaser);

    if (isPromotionsMode && isPromotionsFilterInfo(filterInfo)) {
      addSearchTag(addTag);
      addArrayFilterTag(addTag, "promoType", "Promo Type", filterInfo.promoType);
      addArrayFilterTag(addTag, "promoStatus", "Status", filterInfo.promoStatus);
      addStartDateTag(addTag);
    } else if (isDisputeTrackingMode && isDisputeTrackingFilterInfo(filterInfo)) {
      addSearchTag(addTag);
      addArrayFilterTag(addTag, "disputeTrackingStatus", "Status", filterInfo.disputeTrackingStatus);
    } else if (isAccountingRemittanceMode && isAccountingRemittanceFilterInfo(filterInfo)) {
      addSearchTag(addTag);
    } else {
      addArrayFilterTag(addTag, "dc", "DC", "dc" in filterInfo ? filterInfo.dc : undefined);

      if (isApprovalMode && isApprovalFilterInfo(filterInfo)) {
        addArrayFilterTag(addTag, "glCode", "GL Code", filterInfo.glCode);
        addArrayFilterTag(addTag, "analyst", "Analyst", filterInfo.analyst);
        addArrayFilterTag(addTag, "approver", "Approver", filterInfo.approver);
        addSearchTag(addTag);
      } else if (!isApprovalMode && !isPromotionsMode && "priority" in filterInfo) {
        const deductionFilterInfo = filterInfo;
        addArrayFilterTag(addTag, "priority", "Priority", deductionFilterInfo.priority);
        addArrayFilterTag(addTag, "validity", "Validity", deductionFilterInfo.validity);
        addArrayFilterTag(addTag, "adjustmentTypes", "Deduction Type", deductionFilterInfo.adjustmentTypes);
        addArrayFilterTag(
          addTag,
          "adjustmentCategories",
          "Adjustment Category",
          deductionFilterInfo.adjustmentCategories
        );
        addArrayFilterTag(
          addTag,
          "deductionTypeCategories",
          "Deduction Category",
          deductionFilterInfo.deductionTypeCategories
        );
        addPoNumberTag(addTag, deductionFilterInfo);
        addArrayFilterTag(addTag, "retailer", "Retailer", deductionFilterInfo.retailer);
        addArrayFilterTag(addTag, "analyst", "Analyst", filterInfo.analyst);
        addArrayFilterTag(addTag, "approver", "Approver", filterInfo.approver);
        addArrayFilterTag(addTag, "progress", "Progress", filterInfo.progress);
      }
    }

    return tagsData;
  }, [
    filterInfo,
    isApprovalMode,
    isPromotionsMode,
    isDisputeTrackingMode,
    isAccountingRemittanceMode,
    createTag,
    addCheckDateTag,
    addInvoiceDateTag,
    addArrayFilterTag,
    addPoNumberTag,
    addSearchTag,
    addStartDateTag,
    getTooltipContent,
  ]);

  const handleDelete = useCallback(
    async (key: string) => {
      const newFilterInfo = applyFilterKeyDeletion(filterInfo, key);
      if (!newFilterInfo) return;

      const isDefault = (isDefaultFilter as FilterSetApi["isDefaultFilter"])(newFilterInfo);
      setIsFilterApplied(!isDefault);
      setFilter(newFilterInfo);
      setCurrentPage(1);
      setDrawerOpen(false);
      await searchHandler();
    },
    [filterInfo, isDefaultFilter, setIsFilterApplied, setFilter, setCurrentPage, setDrawerOpen, searchHandler]
  );

  const handleClearFilters = async () => {
    clearFilter(defaultFilterInfo);
    setCurrentPage(1);
    await searchHandler();
  };

  const nonDeletableKeys = useMemo(() => {
    if (isApprovalMode || isAccountingRemittanceMode) return [];
    if (isNoDefaultDateRange) return [];
    return ["checkDate"];
  }, [isApprovalMode, isAccountingRemittanceMode, isNoDefaultDateRange]);

  return (
    <Flex justifyContent="space-between" align="center" direction="row" className={className}>
      <Division className="flex-1" style={{ overflow: "hidden" }}>
        <TagNavigator
          tagsData={tagsFilterData}
          onDelete={handleDelete}
          nonDeletableKeys={nonDeletableKeys}
        />
      </Division>
      <Division className="ps-2">
        {isFilterApplied && (
          <Button size="small" variant="clear" color="grey" onPress={handleClearFilters} isDisabled={!isFilterApplied}>
            <Icon name={Icons.CloseCircle} size="small" slot="start" />
            Clear filters
          </Button>
        )}
      </Division>
    </Flex>
  );
};

export default DeductionFilterSet;
